import { z } from "zod";

export const MAX_KIT_COMPONENTS = 20;
export const MAX_COMPONENT_QUANTITY = 1000;

/** Composition of a kit variant: replaces the whole list. An empty list turns the kit back into an article. */
export const variantCompositionSchema = z.object({
  items: z
    .array(
      z.object({
        componentVariantId: z.uuid("errors.field.required"),
        quantity: z.coerce
          .number()
          .int("errors.field.number")
          .min(1, "errors.field.positive")
          .max(MAX_COMPONENT_QUANTITY, "errors.field.number"),
      }),
    )
    .max(MAX_KIT_COMPONENTS)
    .refine((items) => new Set(items.map((i) => i.componentVariantId)).size === items.length, "errors.field.duplicateComponent"),
});
export type VariantCompositionInput = z.input<typeof variantCompositionSchema>;
export type VariantCompositionData = z.output<typeof variantCompositionSchema>;

export interface CompositionPart {
  sku: string;
  quantity: number;
}

/**
 * Composition written in a spreadsheet cell: components separated by " + ", each with an optional
 * "N x " prefix — `PHGPB-LOR-S/M + PHCUMR-L/XL`, `2 x REF-A + REF-B`. SKUs may contain "/" and "-";
 * they are upper-cased like everywhere else. A repeated SKU adds up. Null when unreadable.
 */
export function parseComposition(text: string): CompositionPart[] | null {
  const parts = text.split(/\s+\+\s+/).map((part) => part.trim());
  const byKey = new Map<string, number>();
  for (const part of parts) {
    const match = /^(?:(\d+)\s*[x×*]\s+)?(\S.*)$/i.exec(part);
    // A dangling separator ("REF-A +") is a half-written cell, not a reference.
    if (!match || /(^|\s)\+(\s|$)/.test(part)) return null;
    const quantity = match[1] === undefined ? 1 : Number.parseInt(match[1], 10);
    const sku = match[2]!.trim().toUpperCase();
    if (quantity < 1 || quantity > MAX_COMPONENT_QUANTITY || sku.length > 64) return null;
    byKey.set(sku, (byKey.get(sku) ?? 0) + quantity);
  }
  if (byKey.size === 0 || byKey.size > MAX_KIT_COMPONENTS) return null;
  return [...byKey].map(([sku, quantity]) => ({ sku, quantity }));
}

/**
 * Splits a product whose site variants combine two sizes (vest "S/M" · cummerbund "XS") into one
 * ERP article per option axis; each site variant becomes a kit of one article of each axis. The
 * share spreads the variant's cost, price and weight over the axes (sums to 100 %).
 */
export const splitVariantsSchema = z
  .object({
    dryRun: z.boolean().default(true),
    axes: z
      .array(
        z.object({
          title: z.string().trim().min(1, "errors.field.required").max(200, "errors.field.tooLong"),
          shareBp: z.coerce
            .number()
            .int("errors.field.number")
            .min(0, "errors.field.positive")
            .max(10_000, "errors.field.number"),
        }),
      )
      .max(3)
      .optional(),
  })
  .refine((v) => !v.axes || v.axes.reduce((s, a) => s + a.shareBp, 0) === 10_000, {
    path: ["axes"],
    message: "errors.field.shareTotal",
  });
export type SplitVariantsInput = z.input<typeof splitVariantsSchema>;
export type SplitVariantsData = z.output<typeof splitVariantsSchema>;

export interface KitPricePart {
  /** Units of the component in one kit. */
  quantity: number;
  /** The component's own sale price, which sets its share of the kit price. */
  salePriceCents: number;
}

/**
 * Unit prices of a kit's components so that one kit still sells at `kitUnitCents`: the kit price is
 * shared in proportion to the components' own prices (equally per unit when none has one), and the
 * rounding left over goes to a component sold by the unit, so the total stays exact whenever one is.
 */
export function splitKitPrice(kitUnitCents: number, parts: KitPricePart[]): number[] {
  if (parts.length === 0) return [];
  const weights = parts.map((p) => p.quantity * Math.max(0, p.salePriceCents));
  const byPrice = weights.some((w) => w > 0);
  const weightOf = (i: number) => (byPrice ? weights[i]! : parts[i]!.quantity);
  const total = parts.reduce((s, _, i) => s + weightOf(i), 0);
  const units = parts.map((p, i) => Math.round((kitUnitCents * weightOf(i)) / total / p.quantity));
  const rest = kitUnitCents - units.reduce((s, u, i) => s + u * parts[i]!.quantity, 0);
  const single = parts.findLastIndex((p) => p.quantity === 1);
  const target = single >= 0 ? single : parts.length - 1;
  units[target] = units[target]! + Math.trunc(rest / parts[target]!.quantity);
  return units;
}
